/**
 * SectionCard — patrón visual unificado para CADA sección del expediente.
 *
 * Estructura:
 *   ┌───────────────────────────────────────────────────────────┐
 *   │ [ICON-CHIP color suave] Título de la sección       meta │  ← cabecera
 *   ├───────────────────────────────────────────────────────────┤
 *   │ children…                                                 │  ← cuerpo
 *   └───────────────────────────────────────────────────────────┘
 *
 * - El `tone` controla el color del icon-chip (color suave del sistema
 *   unificado: brand / teal / blue / amber / red / purple / gray).
 * - `meta` es un nodo opcional alineado a la derecha (un contador, un
 *   chip de estado, etc.).
 * - `testid` se aplica al wrapper y `body-testid` opcional al cuerpo.
 * - `padded={false}` desactiva el padding del cuerpo (útil para componentes
 *   internos que ya gestionan su propio padding, como GestionesBlock).
 */
import { forwardRef } from "react";

const TONE_CLASSES = {
  brand:  "chip-icon-amber",
  teal:   "chip-icon-teal",
  blue:   "chip-icon-blue",
  amber:  "chip-icon-yellow",
  red:    "chip-icon-red",
  purple: "chip-icon-purple",
  gray:   "chip-icon-gray",
};

const SectionCard = forwardRef(function SectionCard({
  icon: Icon,
  title,
  titleBadge = null,
  tone = "gray",
  meta = null,
  children,
  className = "",
  bodyClassName = "",
  padded = true,
  testid,
}, ref) {
  const toneCls = TONE_CLASSES[tone] || TONE_CLASSES.gray;
  return (
    <section
      ref={ref}
      data-testid={testid}
      className={`bg-white border border-border rounded-lg overflow-hidden ${className}`}
    >
      <header className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 border-b border-border/70">
        <div className="flex items-center gap-2.5 min-w-0">
          {Icon && (
            <span className={`icon-chip ${toneCls} flex-shrink-0`}>
              <Icon className="w-4 h-4" weight="regular" />
            </span>
          )}
          <h3 className="section-title truncate">{title}</h3>
          {titleBadge && (
            <span className="flex-shrink-0">{titleBadge}</span>
          )}
        </div>
        {meta && (
          <div className="flex items-center gap-2 text-[11.5px] text-muted flex-shrink-0">
            {meta}
          </div>
        )}
      </header>
      <div className={padded ? `px-4 md:px-5 py-4 ${bodyClassName}` : bodyClassName}>
        {children}
      </div>
    </section>
  );
});

export default SectionCard;
